import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { EmptyState } from '@/components/EmptyState';
import { ConfirmDialog } from '@/components/Modal';

/** `/settings/field-definitions` (v0.4 章③、D-29/D-67/D-70)。
 *  マスタ項目 (field_definition_catalog) の必須/任意・表示可否をテナント別に上書きする
 *  (tenant_field_definitions)。設計: docs/design/v04-shift-labor-management-detail.md §3-5
 *  GET /field-definitions は全認証ユーザーに開放されているが、実際に変更できるのは
 *  会社管理者以上 + tenant_field_required フラグ ON のみ (PATCH/impact が 403 で弾く)。 */

type FieldDefinitionRow = {
  entity: string;
  field: string;
  label_key: string;
  data_kind: 'text' | 'number' | 'date' | 'ref' | 'bool' | 'json';
  required_on_create: boolean;
  required_on_update: boolean;
  is_visible: boolean;
  sort_order: number;
  is_required_overridable: boolean;
  is_visibility_overridable: boolean;
};

type ImpactResult = { affected_rows: number; sample: { id: string; label: string }[] };

const DATA_KIND_LABELS: Record<FieldDefinitionRow['data_kind'], string> = {
  text: 'テキスト', number: '数値', date: '日付', ref: '参照', bool: '真偽値', json: 'JSON',
};

const NOT_OVERRIDABLE_TITLE = 'この項目は構造上必須のため変更できません';

function FieldDefinitionRowView({ row, onSaved }: { row: FieldDefinitionRow; onSaved: () => void }) {
  const [confirmImpact, setConfirmImpact] = useState<ImpactResult | null>(null);
  const [checkingImpact, setCheckingImpact] = useState(false);

  const patchMut = useMutation({
    mutationFn: (body: Partial<Pick<FieldDefinitionRow, 'required_on_create' | 'required_on_update'>>) =>
      apiFetch(`/field-definitions/${encodeURIComponent(row.entity)}/${encodeURIComponent(row.field)}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => { onSaved(); toast.success('更新しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '更新に失敗しました'),
  });

  const handleRequiredOnCreateChange = (checked: boolean) => {
    patchMut.mutate({ required_on_create: checked });
  };

  // D-70 必須要件②: 更新時必須を false→true にする操作だけ、事前に影響件数を確認する
  // (作成時必須の変更・更新時必須をOFFにする操作は既存データを不正にしないため確認不要)
  const handleRequiredOnUpdateChange = async (checked: boolean) => {
    if (!checked) {
      patchMut.mutate({ required_on_update: false });
      return;
    }
    setCheckingImpact(true);
    try {
      const impact = await apiFetch<ImpactResult>(
        `/field-definitions/${encodeURIComponent(row.entity)}/${encodeURIComponent(row.field)}/impact`,
      );
      if (impact.affected_rows > 0) {
        setConfirmImpact(impact);
      } else {
        patchMut.mutate({ required_on_update: true });
      }
    } catch (e) {
      toast.error(e instanceof ApiClientError ? e.message : '影響件数の取得に失敗しました');
    } finally {
      setCheckingImpact(false);
    }
  };

  const disableRequired = !row.is_required_overridable || patchMut.isPending || checkingImpact;

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 py-3 px-3 sm:px-4">
        <div className="flex-1 min-w-0">
          <div className="text-sm text-text-body font-mono">{row.field}</div>
          <div className="text-xs text-text-sub mt-0.5">
            {DATA_KIND_LABELS[row.data_kind] ?? row.data_kind}
            {!row.is_visible && '・非表示'}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 shrink-0">
          <label
            className={`flex items-center gap-2 text-sm min-h-[44px] ${disableRequired ? 'text-text-sub' : ''}`}
            title={!row.is_required_overridable ? NOT_OVERRIDABLE_TITLE : undefined}
          >
            {/* v0.4 章③ Design レビュー反映 (Critical): 素の checkbox (20x20px) は
                window.__designCheck.checkTapTargetSize() に確実に抵触する (input要素を直接
                走査するため、包む label の min-h-[44px] だけでは判定を通らない)。
                box-content + padding で見た目の20pxチェックボックスはそのまま、
                実際のヒットボックス (border-box) だけ44x44pxへ拡張する */}
            <input
              type="checkbox"
              className="w-5 h-5 p-[12px] box-content"
              checked={row.required_on_create}
              disabled={disableRequired}
              onChange={(e) => handleRequiredOnCreateChange(e.target.checked)}
            />
            必須（作成時）
          </label>
          <label
            className={`flex items-center gap-2 text-sm min-h-[44px] ${disableRequired ? 'text-text-sub' : ''}`}
            title={!row.is_required_overridable ? NOT_OVERRIDABLE_TITLE : undefined}
          >
            <input
              type="checkbox"
              className="w-5 h-5 p-[12px] box-content"
              checked={row.required_on_update}
              disabled={disableRequired}
              onChange={(e) => handleRequiredOnUpdateChange(e.target.checked)}
            />
            必須（更新時）
          </label>
        </div>
      </div>

      <ConfirmDialog
        open={confirmImpact !== null}
        title="必須項目に変更しますか？"
        confirmLabel="変更する"
        loading={patchMut.isPending}
        onClose={() => setConfirmImpact(null)}
        onConfirm={() => {
          patchMut.mutate({ required_on_update: true });
          setConfirmImpact(null);
        }}
        message={
          <div className="space-y-2">
            <p>
              この項目を必須にすると、既存の {confirmImpact?.affected_rows ?? 0} 件のレコードが編集できなくなります。よろしいですか？
            </p>
            {confirmImpact && confirmImpact.sample.length > 0 && (
              <ul className="text-xs text-text-sub list-disc pl-4 space-y-0.5">
                {confirmImpact.sample.slice(0, 5).map((s) => <li key={s.id}>{s.label}</li>)}
              </ul>
            )}
          </div>
        }
      />
    </>
  );
}

export function FieldDefinitionsSettingsPage() {
  const qc = useQueryClient();
  const [expandedEntities, setExpandedEntities] = useState<Set<string>>(new Set());

  const { data, isLoading, isError, error } = useQuery<FieldDefinitionRow[]>({
    queryKey: ['field-definitions'],
    queryFn: () => apiFetch<FieldDefinitionRow[]>('/field-definitions'),
  });

  // v0.4 章③ Design レビュー反映 (Major): 見出しに生の snake_case 識別子 (例: 'delivery_service_master')
  // をそのまま出すと Nielsen H2 (real-world match) 違反かつ姉妹画面 LabelsSettingsPage.tsx との
  // 一貫性が崩れる (H4)。同じ label_key_catalog (category='entity') を参照して解決する。
  // /labels/catalog は会社管理者以上限定のため、このページに非admin (URL直叩き) で来た場合は
  // 403で取得失敗するが、その場合は生の entity 文字列へ自然にフォールバックする (エラーで落とさない)
  const { data: labelCatalog } = useQuery<Array<{ entity: string | null; category: string; current_text: string | null; default_label: string }>>({
    queryKey: ['labels-catalog'],
    queryFn: () => apiFetch('/labels/catalog'),
    retry: false,
  });
  const entityDisplayName = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of labelCatalog ?? []) {
      if (r.category !== 'entity' || !r.entity) continue;
      map.set(r.entity, r.current_text ?? r.default_label);
    }
    return map;
  }, [labelCatalog]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['field-definitions'] });

  const rows = data ?? [];
  const groups = useMemo(() => {
    const map = new Map<string, FieldDefinitionRow[]>();
    for (const r of rows) {
      const list = map.get(r.entity);
      if (list) list.push(r); else map.set(r.entity, [r]);
    }
    return map;
  }, [rows]);

  const toggleEntity = (entity: string) => {
    setExpandedEntities((prev) => {
      const next = new Set(prev);
      if (next.has(entity)) next.delete(entity); else next.add(entity);
      return next;
    });
  };

  if (isError) {
    if (error instanceof ApiClientError && error.status === 403) {
      return (
        <div className="p-6 border border-surface-border rounded-lg bg-white text-text-sub">
          権限がありません。
        </div>
      );
    }
    return (
      <div className="p-6 border border-surface-border rounded-lg bg-white text-text-sub">
        入力項目設定の取得に失敗しました。
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-h2">入力項目の必須設定</h2>
        <p className="text-sm text-text-sub mt-1">
          マスタ画面の各項目を、作成時・更新時それぞれで必須にするかどうかを設定します。
        </p>
      </div>

      {isLoading ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : rows.length === 0 ? (
        <EmptyState message="設定可能な項目がありません" />
      ) : (
        <div className="space-y-2">
          {[...groups.entries()].map(([entity, list]) => {
            const expanded = expandedEntities.has(entity);
            return (
              <div key={entity} className="border border-surface-border rounded-lg bg-white">
                <button
                  type="button"
                  onClick={() => toggleEntity(entity)}
                  aria-expanded={expanded}
                  className="w-full flex items-center justify-between gap-2 px-3 sm:px-4 py-3 min-h-[44px] text-left"
                >
                  {/* v0.4 章③ Design レビュー反映 (Major): 320px幅で最長のentity名
                      (delivery_service_master 等) が右側の件数+アイコンと衝突しうるため
                      min-w-0 truncate / shrink-0 で安全域を確保する */}
                  <span className="min-w-0 truncate text-sm font-semibold text-text-body">
                    {entityDisplayName.get(entity) ?? entity}
                  </span>
                  <span className="flex items-center gap-2 text-xs text-text-sub shrink-0">
                    {list.length}項目
                    <ChevronDown size={16} aria-hidden className={`transition-transform ${expanded ? 'rotate-180' : ''}`} />
                  </span>
                </button>
                {expanded && (
                  <div className="border-t border-surface-border divide-y divide-surface-border">
                    {list.map((row) => (
                      <FieldDefinitionRowView key={`${row.entity}.${row.field}`} row={row} onSaved={invalidate} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
